import { useState } from 'react';
import { Content, Header, Page } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import './Olympus.css';

const ZEUS_PROJECT_ID = 55372; // MGMTAPPPRYMANAGEMENT/olympus/zeus
const ANTHROPIC_MODEL = 'mgmt-app-claude-haiku-4-5';
const SOLUTIONS = ['management', 'tech-community'] as const;
type Solution = (typeof SOLUTIONS)[number];

interface PipelineResult {
  id: number;
  web_url: string;
  status: string;
}

export const OlympusStartPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [requirement, setRequirement] = useState('');
  const [solution, setSolution] = useState<Solution>('management');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<PipelineResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = requirement.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const proxyBase = await discoveryApi.getBaseUrl('proxy');
      const url = `${proxyBase}/olympus-trigger/projects/${ZEUS_PROJECT_ID}/pipeline`;
      const res = await fetchApi.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ref: 'main',
          variables: [
            { key: 'REQUIREMENT', value: requirement.trim() },
            { key: 'SOLUTION', value: solution },
            { key: 'ANTHROPIC_MODEL', value: ANTHROPIC_MODEL },
          ],
        }),
      });
      if (!res.ok) {
        const body = await res.text();
        setError(`No se pudo disparar el pipeline (status ${res.status}). ${body}`);
        return;
      }
      const data = (await res.json()) as PipelineResult;
      setResult(data);
      setRequirement('');
    } catch (e: any) {
      setError(e.message ?? 'Error desconocido disparando el pipeline.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header title="Olympus Start" subtitle="Registrar un nuevo requerimiento para Zeus" />
      <Content>
        <div className="olympus-page">
          <div className="olympus-main" style={{ maxWidth: 640 }}>
            <div className="olympus-trigger-panel open">
              <div className="olympus-trigger-header" style={{ cursor: 'default' }}>
                <h3>
                  <span className="dot" /> Nuevo requerimiento
                </h3>
              </div>
              <div className="olympus-trigger-body">
                <textarea
                  placeholder='Describe el requerimiento en lenguaje natural… ej: "Modifica el catálogo de negocio para incluir el departamento"'
                  rows={5}
                  value={requirement}
                  onChange={e => setRequirement(e.target.value)}
                  disabled={submitting}
                />
                <div className="olympus-trigger-row">
                  <select
                    className="olympus-sol-select"
                    value={solution}
                    onChange={e => setSolution(e.target.value as Solution)}
                    disabled={submitting}
                  >
                    {SOLUTIONS.map(sol => (
                      <option key={sol} value={sol}>
                        {sol}
                      </option>
                    ))}
                  </select>
                  <span className="olympus-trigger-hint">POST /projects/{ZEUS_PROJECT_ID}/pipeline</span>
                  <button
                    className="olympus-btn-trigger"
                    style={{ marginLeft: 'auto' }}
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                  >
                    {submitting ? 'Disparando…' : 'Disparar Zeus'}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div
                style={{
                  border: '1px solid #f85149',
                  background: '#3d1214',
                  color: '#f85149',
                  borderRadius: 10,
                  padding: 14,
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            {result && (
              <div
                style={{
                  border: '1px solid #3fb950',
                  background: '#0d2e1a',
                  color: '#e6edf3',
                  borderRadius: 10,
                  padding: 14,
                  fontSize: 13,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ color: '#3fb950', fontWeight: 600 }}>
                  Pipeline disparado (#{result.id}) — status: {result.status}
                </div>
                <a href={result.web_url} target="_blank" rel="noopener noreferrer">
                  Ver pipeline en GitLab →
                </a>
                <a href="/olympus">Ver progreso en Olympus →</a>
              </div>
            )}
          </div>
        </div>
      </Content>
    </Page>
  );
};
